import axios from 'axios';
import { SearchResult, PaginatedResponse } from '../types';

// Use API URL with correct path - files search endpoint is at /api/v1/files/search
const getSearchApiUrl = () => {
  let baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  // Remove /api/v1 suffix if present (we'll add the correct path ourselves)
  if (baseUrl.endsWith('/api/v1')) {
    baseUrl = baseUrl.replace(/\/api\/v1$/, '');
  }
  return baseUrl;
};

interface SearchResponse extends PaginatedResponse<SearchResult> {
  facets?: Record<string, Array<{ value: string; count: number }>>;
  searchTime?: number;
  query: string;
  searchType: string;
}

interface SearchSuggestion {
  text: string;
  type: 'phrase' | 'category' | 'tag' | 'document_title';
  score: number;
}

interface SearchFilters {
  categories?: string[];
  tags?: string[];
  fileTypes?: string[];
  dateRange?: {
    start?: string;
    end?: string;
  };
  uploadedBy?: string[];
}

class SearchService {
  private searchApi = axios.create({
    baseURL: getSearchApiUrl(),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Add auth token to search requests
    this.searchApi.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  // Perform search using the backend /api/v1/files/search endpoint
  async search(params: {
    query: string;
    searchType?: 'basic' | 'semantic' | 'exact';
    filters?: SearchFilters;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    includeFacets?: boolean;
  }): Promise<SearchResponse> {
    try {
      // Backend uses GET /api/v1/files/search with query params
      const searchParams = new URLSearchParams({
        q: params.query,
        limit: String(params.pageSize || 20),
        offset: String(((params.page || 1) - 1) * (params.pageSize || 20)),
      });

      // Add category filter if present
      if (params.filters?.categories?.length) {
        searchParams.append('category', params.filters.categories[0]);
      }

      const response = await this.searchApi.get(`/api/v1/files/search?${searchParams}`);

      // Backend returns List[SearchResult] directly (array, not object)
      const results = Array.isArray(response.data) ? response.data : (response.data.results || []);
      const transformedResults: SearchResult[] = results.map((item: any) => ({
        id: item.id || item.document_id || item.file_id || Math.random().toString(),
        title: item.title || item.file_name || 'Untitled',
        description: item.snippet || item.description || item.content || '',
        category: item.category || 'design',
        tags: item.tags || [],
        uploadedBy: item.uploaded_by || 'Unknown',
        uploadedAt: item.uploaded_at || new Date().toISOString(),
        mimeType: item.mime_type || 'application/pdf',
        fileSize: item.file_size || 0,
        score: item.relevance_score || item.score || 0,
        highlights: item.highlights,
      }));

      return {
        results: transformedResults,
        total: transformedResults.length,
        page: params.page || 1,
        pageSize: params.pageSize || 20,
        totalPages: Math.ceil(transformedResults.length / (params.pageSize || 20)),
        facets: undefined,
        query: params.query,
        searchType: params.searchType || 'basic',
      };
    } catch (error: any) {
      console.error('Search failed:', error);
      throw new Error(error.response?.data?.message || 'Search failed');
    }
  }

  // ADR-005: Stub methods — backend endpoints not implemented.
  // Kept as no-op stubs because callers (SearchBar, SearchPage) reference them.
  // TODO(Sprint 2+): Implement or remove callers.

  async getSuggestions(_query: string, _limit: number = 10): Promise<SearchSuggestion[]> {
    return [];
  }

  async getAutocomplete(_query: string, _field?: string): Promise<string[]> {
    return [];
  }

  async trackSearch(_searchEvent: {
    query: string;
    searchType: string;
    resultsCount: number;
    filters?: SearchFilters;
    clickedResults?: string[];
    sessionId?: string;
  }): Promise<void> {
    // No-op: analytics endpoint not yet implemented
  }

  async trackClick(_clickEvent: {
    searchId?: string;
    documentId: string;
    resultPosition: number;
    query: string;
  }): Promise<void> {
    // No-op: click tracking endpoint not yet implemented
  }

  async getPopularSearches(_timeRange?: 'day' | 'week' | 'month'): Promise<
    Array<{ query: string; count: number }>
  > {
    return [];
  }

  async vectorSearch(_params: {
    query: string;
    topK?: number;
    filters?: SearchFilters;
    threshold?: number;
  }): Promise<SearchResponse> {
    // ADR-005: vector search will be routed through RetrievalService
    throw new Error('Vector search not yet available — use standard search');
  }

  async hybridSearch(_params: {
    query: string;
    alpha?: number;
    filters?: SearchFilters;
    page?: number;
    pageSize?: number;
  }): Promise<SearchResponse> {
    // ADR-005: hybrid search will be routed through RetrievalService
    throw new Error('Hybrid search not yet available — use standard search');
  }
}

export const searchService = new SearchService();
export default searchService;
